import axios from 'axios';
import * as pdfjs from 'pdfjs-dist/webpack';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PoHistoryDetails from 'src/components/PoHistory';
import Button from 'src/components/design-system/Button';
import PrintDetails from 'src/components/design-system/PrintDetails';
import { CanUsesInitialBudgets } from 'src/constants/permissionsList';
import useGuideMe from 'src/hooks/useGuideMe';
import usePermission from 'src/hooks/usePermissions';
import useWindowSize from 'src/hooks/useWindowSize';
import { generateGlCodeLabel } from 'src/services/useInvoiceGlBudgetSummaryApi';
import usePoHistoryApi from 'src/services/usePoHistoryApi';
import { validSignedUrl } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import { isImage } from '../receipt_details/PrintPDF';
import CancelInvoice from './CancelInvoice';
import CancelPo from './CancelPo';
import ChangePoAssignment from './ChangePoAssignment';
import ClosePo from './ClosePo';
import Complete from './Complete';
import Decline from './Decline';
import DeleteInvoice from './DeleteInvoice';
import { guideMeSteps } from './GuideMeSteps';
import { InvoiceContext } from './InvoiceDetailsContext';
import Recall from './Recall';
import SaveInvoice from './SaveInvoice';
import SubmitForApprovalDialog from './SubmitForApprovalDialog';
import UnDeleteInvoice from './UnDeleteInvoice';
import PDF from './print/PDF';

const InvoiceDetailsActions: FC = () => {
  const { guideMe, openGuideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'invoice-details-guide',
  });

  const showScans = useContextSelector(InvoiceContext, (value) => value.showScans);
  const setShowScans = useContextSelector(InvoiceContext, (value) => value.setShowScans);
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const initialInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.initialInvoiceLineItems
  );
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const poDetails = useContextSelector(InvoiceContext, (value) => value.poDetails);
  const throttledInvoiceGLCodes = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceGLCodes
  );

  const { poStatus } = invoice;
  const [searchParams] = useSearchParams();
  const poId = searchParams.get('poid') || '';
  const { poHistory, getPoHistory, isLoading: isPoHistoryLoading } = usePoHistoryApi(poId, false);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);

  useEffect(() => {
    openGuideMe();
  }, [openGuideMe]);

  const [state, setState] = useState({
    history: false,
    notes: false,
    attachments: false,
  });

  useEffect(() => {
    if (state.history) {
      getPoHistory();
    }
  }, [getPoHistory, state.history]);

  const isInvoiceMPA = useContextSelector(InvoiceContext, (value) => value.isInvoiceMPA);
  const GlDistributionData = useContextSelector(InvoiceContext, (value) => value.invoiceGlCodes);

  const hasUsesInitialBudgetPermission = usePermission({
    permissions: CanUsesInitialBudgets,
    companyAttribute: true,
  });
  const flattenedItems = useMemo(
    () => GlDistributionData.filter((e) => e?.glLabel !== 'Totals:')?.flatMap((obj) => obj?.items),
    [GlDistributionData]
  );
  const glCodesObject = useContextSelector(InvoiceContext, (value) => value.glCodesObject);

  const invoiceProcessedGlCodes = useMemo(
    () =>
      throttledInvoiceGLCodes.map((glCode) => {
        const label = generateGlCodeLabel(
          glCode.glLabel,
          glCodesObject?.[glCode?.glCode as string],
          hasUsesInitialBudgetPermission
        );
        return {
          ...glCode,
          glLabel: label,
        };
      }),
    [glCodesObject, hasUsesInitialBudgetPermission, throttledInvoiceGLCodes]
  );

  const attachDocuments = initialInvoice?.attachments?.map((attachment) => attachment?.signedUrl);
  const [resolveAttachmentDocs, setResolveAttachmentDocs] = useState<string[]>([]);

  const pdf = useMemo(
    () => (
      <PDF
        isHistoryIncluded={state.history}
        isNotesIncluded={state.notes}
        invoice={initialInvoice}
        invoiceId={invoiceId}
        invoiceLineItems={initialInvoiceLineItems}
        poHistory={poHistory}
        title={title}
        invoiceGlCodes={invoiceProcessedGlCodes}
        showAttachment={state?.attachments}
        attachDocuments={initialInvoice?.attachments?.map((attachment) => attachment?.signedUrl)}
        isPoHistoryLoading={isPoHistoryLoading}
        setIsPrintLoading={setIsPrintLoading}
        isInvoiceMPA={isInvoiceMPA}
        invoiceMPAData={flattenedItems}
        resolveAttachmentDocs={resolveAttachmentDocs}
        poDetails={poDetails}
      />
    ),
    [
      flattenedItems,
      initialInvoice,
      initialInvoiceLineItems,
      invoiceId,
      invoiceProcessedGlCodes,
      isInvoiceMPA,
      isPoHistoryLoading,
      poDetails,
      poHistory,
      resolveAttachmentDocs,
      state?.attachments,
      state.history,
      state.notes,
      title,
    ]
  );

  const isNotLoading = poStatus?.toLowerCase()?.includes('open') && invoice;
  const { width } = useWindowSize();

  const getUrl = useCallback(async (src: string) => {
    const response = await axios.get(src, { responseType: 'blob' });
    const blob: Blob = response.data;
    return window.URL.createObjectURL(blob);
  }, []);

  const renderPage = useCallback(async (data: string) => {
    const imagesList: string[] = [];
    const canvas = document.createElement('canvas');
    canvas.setAttribute('class', 'canv');
    const pdf = await pdfjs.getDocument({ data }).promise; //pics
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = {
        canvasContext: canvas.getContext('2d'),
        viewport: viewport,
      };
      await page.render(renderContext).promise;
      const img = canvas.toDataURL('image/png');
      imagesList.push(img);
    }
    return imagesList;
  }, []);

  const UrlUploader = useCallback(
    async (url: string): Promise<string[]> => {
      try {
        const blobUrl = await getUrl(url);
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        return await new Promise<string[]>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (e: ProgressEvent<FileReader>) => {
            try {
              const data: string = atob(e.target!.result!.toString().replace(/.*base64,/, ''));
              const imageList = await renderPage(data);
              resolve(imageList);
            } catch (error) {
              reject(error);
            }
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        // Handle any errors here
        console.error(error);
        throw error; // Rethrow the error to propagate it
      }
    },
    [getUrl, renderPage]
  );

  const addDocuments = useCallback(() => {
    if (!state?.attachments && attachDocuments.length > 0) {
      setIsPrintLoading(true);
      Promise.all(
        attachDocuments.map((url) => {
          if (!isImage(url)) {
            return UrlUploader(`${validSignedUrl(url)}`);
          } else {
            return getUrl(`${validSignedUrl(url)}`);
          }
        })
      )
        .then((res) => {
          const flattenedArray = res.reduce((acc, curr) => {
            if (typeof curr === 'string') {
              return [...acc, curr];
            }
            return [...acc, ...curr];
          }, []);
          setResolveAttachmentDocs(
            typeof flattenedArray === 'string' ? ([] as string[]) : flattenedArray
          );
        })
        .catch((err) => console.error(err))
        .finally(() => setTimeout(() => setIsPrintLoading(false), 1000));
    }
  }, [UrlUploader, attachDocuments, getUrl, state.attachments]);

  return width > 1300 ? (
    <div className="flex justify-between">
      {isNotLoading && <SaveInvoice />}
      {isNotLoading && (
        <div className="flex">
          <Complete />
          <SubmitForApprovalDialog />
          <ChangePoAssignment />
          <Decline />
          <Recall />
          <CancelInvoice />
          <DeleteInvoice />
          <UnDeleteInvoice />
          <ClosePo />
          <CancelPo />
        </div>
      )}
      <div className={`flex ${!isNotLoading ? 'flex-grow justify-end' : ''}`}>
        <PoHistoryDetails title={title} />
        <PrintDetails
          pdf={pdf}
          setState={setState}
          state={state}
          documentNumber={invoice?.invoiceNumber}
          title={title}
          isPoHistoryLoading={isPoHistoryLoading}
          isPrintLoading={isPrintLoading}
          setIsPrintLoading={setIsPrintLoading}
          addDocuments={addDocuments}
        />
        {guideMe}
        <Button
          color="light"
          onClick={() => setShowScans((prev: boolean) => !prev)}
          endIcon={
            <div className="bg-dark rounded-full h-5 w-5 flex items-center justify-center text-[0.6rem] text-white">
              {invoice?.attachments?.length || 0}
            </div>
          }
        >
          {showScans ? 'Hide Scans' : 'Show Scans'}
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-wrap">
      {isNotLoading && <SaveInvoice />}
      <div className="flex-grow" />
      {isNotLoading && (
        <>
          <Complete />
          <SubmitForApprovalDialog />
          <ChangePoAssignment />
          <Decline />
          <Recall />
          <CancelInvoice />
          <DeleteInvoice />
          <UnDeleteInvoice />
          <ClosePo />
          <CancelPo />
        </>
      )}
      <div className="flex-grow" />
      <PoHistoryDetails title={title} />
      <PrintDetails
        pdf={pdf}
        setState={setState}
        state={state}
        documentNumber={invoice?.invoiceNumber}
        title={title}
        isPoHistoryLoading={isPoHistoryLoading}
        isPrintLoading={isPrintLoading}
        setIsPrintLoading={setIsPrintLoading}
        addDocuments={addDocuments}
      />
      {guideMe}
      <Button
        color="light"
        onClick={() => setShowScans((prev: boolean) => !prev)}
        endIcon={
          <div className="bg-dark rounded-full h-5 w-5 flex items-center justify-center text-[0.6rem] text-white">
            {invoice?.attachments?.length || 0}
          </div>
        }
      >
        {showScans ? 'Hide Scans' : 'Show Scans'}
      </Button>
    </div>
  );
};

export default InvoiceDetailsActions;
